/** @forge-header
 * @id          dateUtils
 * @role        Date formatting utilities | relative time, display formats, nearest-date snap
 * @pattern     pure-fn
 * @purity      none
 * @units       n/a
 * @upstream    none
 * @downstream  multiple components (low-risk utility)
 * @exports     getTimeAgo:fn | getTimeAgoDetailed:fn | formatDate:fn | formatDateTime:fn | getTodayStr:fn | findNearestAvailableDate:fn | isGameLikelyOver:fn
 * @test        dateUtils.test.ts (12 cases)
 */

/**
 * Returns a human-readable relative time string (e.g., "5m ago", "2h ago")
 */
export function getTimeAgo(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);

  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

/**
 * Returns a human-readable relative time with more detail for larger intervals
 */
export function getTimeAgoDetailed(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);

  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)} days ago`;
  return `${Math.floor(seconds / 604800)} weeks ago`;
}

/**
 * Format a date for display in tables/lists
 */
export function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format a date with time
 */
export function formatDateTime(date: Date): string {
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Get today's date as YYYY-MM-DD in local time */
export function getTodayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Find the nearest available date when the selected date has no games.
 *
 * Returns `null` when no snap is needed (selected date already available,
 * or no dates to snap to).
 *
 * Preference order:
 *   1. Today or nearest future date
 *   2. Most recent past date (if all dates are past)
 */
export function findNearestAvailableDate(
  selectedDate: string,
  availableDates: string[],
  today?: string
): string | null {
  if (availableDates.length === 0) return null;
  if (availableDates.includes(selectedDate)) return null; // Already valid

  const todayStr = today ?? getTodayStr();
  const futureOrToday = availableDates.filter((d) => d >= todayStr);

  if (futureOrToday.length > 0) {
    return futureOrToday[0]!;
  }

  // All dates are past — pick the most recent
  return availableDates[availableDates.length - 1]!;
}

/** Estimated game duration in hours by sport (generous to avoid premature hiding) */
const SPORT_DURATION_HOURS: Record<string, number> = {
  nba: 3,
  nfl: 4,
  nhl: 3,
  cbb: 2.5,
  mlb: 4,
  'tennis-atp': 3.5,
  'tennis-wta': 3,
};
const DEFAULT_DURATION_HOURS = 3.5;

/**
 * Returns true if a game has likely finished based on date + Pacific start time + sport duration.
 * Conservative: returns false if start time is unknown (shows the game).
 */
export function isGameLikelyOver(
  gameDate: string,
  startTimePt: string | null | undefined,
  sport?: string
): boolean {
  const now = new Date();

  // Get today's date in Pacific timezone (en-CA locale gives YYYY-MM-DD)
  const todayPt = now.toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });

  // Past dates: definitely over
  if (gameDate < todayPt) return true;

  // Future dates: definitely not over
  if (gameDate > todayPt) return false;

  // Today: check start time + estimated duration
  if (!startTimePt) return false;

  const parts = startTimePt.split(':');
  if (parts.length < 2) return false;
  const startHour = parseInt(parts[0]!, 10);
  const startMin = parseInt(parts[1]!, 10);
  if (isNaN(startHour) || isNaN(startMin)) return false;

  // Current Pacific time as minutes since midnight
  const ptStr = now.toLocaleTimeString('en-US', {
    timeZone: 'America/Los_Angeles',
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
  });
  const ptParts = ptStr.split(':');
  const ptHour = parseInt(ptParts[0]!, 10);
  const ptMin = parseInt(ptParts[1]!, 10);

  const currentMinutes = ptHour * 60 + ptMin;
  const durationHours = sport
    ? (SPORT_DURATION_HOURS[sport] ?? DEFAULT_DURATION_HOURS)
    : DEFAULT_DURATION_HOURS;
  const endMinutes = startHour * 60 + startMin + durationHours * 60;

  return currentMinutes >= endMinutes;
}
